/**
 * Which analyses a single "refresh everything" may actually start.
 *
 * REFRESHING IS PER-ANALYSIS AND THE FOUNDER DOES NOT THINK THAT WAY. Five
 * reports describe one company from one strategy context, and a founder who has
 * just changed that context wants all five to catch up — not to visit five tabs
 * and press the same button on each.
 *
 * The reason this needs a planner rather than a loop is that the five are NOT
 * uniformly runnable, and the differences are invisible from the tab you are on:
 *
 *   - readiness is per-analysis, and routinely mixed (Competition ready while
 *     PESTEL still wants two fields);
 *   - `market_validation` and `exit_strategy` additionally require a BOUND
 *     version — an approved assumptions set and an exit thesis respectively —
 *     and a context field of the same name does not satisfy the server's check.
 *
 * So the honest answer to "refresh everything" is a LIST: what will run, and for
 * each of the rest, why not. Starting a run that the server will refuse spends a
 * round trip to tell the founder nothing, and silently skipping one tells them
 * less than that.
 */

import type { AnalysisType, ServerReadiness } from './types';

/** Every analysis this feature publishes, in the order the tabs present them. */
export const ALL_ANALYSIS_TYPES: readonly AnalysisType[] = [
  'competition',
  'porter',
  'pestel',
  'market_validation',
  'exit_strategy',
];

export type SkipReason =
  /** No approved strategy context exists yet, so nothing can run at all. */
  | 'no_approved_context'
  /** Readiness for THIS analysis is not `ready`. */
  | 'inputs_incomplete'
  /** `market_validation` with no approved assumptions version bound. */
  | 'assumptions_not_bound'
  /** `exit_strategy` with no exit thesis version bound. */
  | 'thesis_not_bound';

export type RefreshPlanEntry =
  | {
    analysisType: AnalysisType;
    willRun: true;
    /** Sent ONLY for the analysis that binds it; see `requestAnalysis`. */
    boundVersionId: string | null;
  }
  | { analysisType: AnalysisType; willRun: false; reason: SkipReason };

export interface BulkRefreshInput {
  /** The approved strategy context every run is built from. */
  approvedContextVersionId: string | null;
  /** Server readiness per analysis. An ABSENT entry is not a blocked one. */
  readiness: Partial<Record<AnalysisType, ServerReadiness | null>> | undefined;
  boundVersions: {
    marketAssumptionVersionId: string | null;
    exitThesisVersionId: string | null;
  } | undefined;
}

/**
 * The plan, in tab order, with one entry per analysis and no omissions.
 *
 * Every analysis appears whether or not it will run, because the caller's job is
 * to tell the founder what happened to all five — a list of two started runs is
 * indistinguishable from a bug.
 */
export function planBulkRefresh(input: BulkRefreshInput): RefreshPlanEntry[] {
  return ALL_ANALYSIS_TYPES.map((analysisType): RefreshPlanEntry => {
    if (!input.approvedContextVersionId) {
      return { analysisType, willRun: false, reason: 'no_approved_context' };
    }

    // ABSENT IS NOT BLOCKED. Readiness arrives with the founder draft, and an
    // analysis the server has not evaluated yet has no entry — treating that as
    // "incomplete" would refuse to refresh anything before the draft loads.
    // This mirrors the per-tab rule exactly (`!!readiness && status !== 'ready'`).
    const readiness = input.readiness?.[analysisType] ?? null;
    if (readiness && readiness.status !== 'ready') {
      return { analysisType, willRun: false, reason: 'inputs_incomplete' };
    }

    if (analysisType === 'market_validation') {
      const bound = input.boundVersions?.marketAssumptionVersionId ?? null;
      if (!bound) return { analysisType, willRun: false, reason: 'assumptions_not_bound' };
      return { analysisType, willRun: true, boundVersionId: bound };
    }

    if (analysisType === 'exit_strategy') {
      const bound = input.boundVersions?.exitThesisVersionId ?? null;
      if (!bound) return { analysisType, willRun: false, reason: 'thesis_not_bound' };
      return { analysisType, willRun: true, boundVersionId: bound };
    }

    return { analysisType, willRun: true, boundVersionId: null };
  });
}

/** One sentence per skip, in the interface's voice. No jargon, no blame. */
export const SKIP_REASON_COPY: Record<SkipReason, string> = {
  no_approved_context: 'Your analysis setup has not been approved yet.',
  inputs_incomplete: 'Some inputs still need your review.',
  assumptions_not_bound: 'Approve a set of market assumptions first.',
  thesis_not_bound: 'Write an exit thesis first.',
};
